import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * ProtectedRoute component that manages access control to routes based on user permissions
 * 
 * @param {object} props Component props
 * @param {React.Component} props.element The component to render if access is granted
 * @param {string[]} [props.requiredRoles] Optional array of roles allowed to access this route
 * @param {string[]} [props.requiredPermissions] Optional array of permissions required
 * @param {boolean} [props.requireAuth=true] Whether authentication is required for this route
 * @returns {React.Component} Either the protected component or a redirect
 */
const ProtectedRoute = ({ 
  element, 
  requiredRoles = [], 
  requiredPermissions = [], 
  requireAuth = true 
}) => {
  const { currentUser, loading } = useContext(AuthContext);
  const location = useLocation();

  // Wait for auth check to complete before making redirect decisions
  if (loading) {
    return null;
  }

  // Not authenticated, redirect to login
  if (requireAuth && !currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If specific roles are required, check if user has any of them
  if (requiredRoles.length > 0) {
    if (!currentUser || !requiredRoles.includes(currentUser.userType)) {
      // Redirect to appropriate dashboard based on user type
      const redirectPath = 
        currentUser?.userType === 'admin' ? '/admin-dashboard' :
        currentUser?.userType === 'staff' ? '/staff-dashboard' : 
        '/dashboard';
      return <Navigate to={redirectPath} replace />;
    }
  }

  // If specific permissions are required, check if user has all of them
  // Both admin and staff must have the required permissions assigned
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every(permission => 
      currentUser && currentUser[permission] === true
    );

    if (!hasAllPermissions) {
      // Redirect to appropriate dashboard based on user type
      const redirectPath = 
        currentUser?.userType === 'admin' ? '/admin-dashboard' :
        currentUser?.userType === 'staff' ? '/staff-dashboard' : 
        '/dashboard';
      return <Navigate to={redirectPath} replace />;
    }
  }

  // User passed all checks, render the protected element
  return element;
};

export default ProtectedRoute;